import { isSameDay } from "date-fns";

import { RefType } from "@/components/molecules/Form/RefInput/utils";
import { defaultDateFormat } from "@/core/date";
import { phoneFormatter } from "@/core/phone";
import { IdentifiableTypes, IdNamePair } from "@/shared/domain";

export enum FieldType {
    PairId = "pairId",
    MultiSelect = "multiSelect",
    String = "string",
    Date = "date",
    Phone = "phone",
}

export interface DataSchemaRaw {
    id: string;
    type: FieldType;
    label?: string;
    size?: string;
    quantity?: number;
    group?: string;
    required?: boolean;
    reference?: RefType;
    readMode?: boolean;
    hideRead?: boolean;
    options?: IdNamePair<IdentifiableTypes>[];
    maxLength?: number;
}

export interface DataSchemaMapped extends DataSchemaRaw {
    value: string;
    readValue: string;
}

interface PanelInfoGroup {
    [key: string]: string[][];
}

export interface FormPanelInfo {
    pending: boolean;
    groups: PanelInfoGroup;
    data: DataSchemaMapped[];
}

export const createFormSchema = (
    schema: DataSchemaRaw[],
    details: any = {},
    pendingDetails: any = null,
): FormPanelInfo => {
    let pending = false;
    const detailsComplete = pendingDetails ? { ...details, ...pendingDetails } : details;
    const groups = {};

    const data = schema.map((element) => {
        const idsPath = element.id.split("-");
        const firstId = idsPath.shift();

        let readValue = details[firstId];
        let value = detailsComplete[firstId];

        idsPath.forEach((id) => {
            readValue = readValue?.[id];
            value = value?.[id];
        });

        switch (element.type) {
            case FieldType.PairId:
                if (readValue?.id !== value?.id) {
                    pending = true;
                }

                readValue = readValue?.name;
                value = value?.id;
                break;
            case FieldType.MultiSelect:
                readValue = [];
                value = [];
                break;
            case FieldType.Date:
                if (!!readValue && !isSameDay(readValue, value)) {
                    pending = true;
                }
                readValue = !!readValue && defaultDateFormat(readValue);
                value = value || null;
                break;
            case FieldType.Phone:
                if (readValue !== value) {
                    pending = true;
                }
                value = phoneFormatter(value);
                readValue = phoneFormatter(readValue);
                break;
            default:
                if (readValue !== value) {
                    pending = true;
                }
                break;
        }

        if (element.group) {
            const [groupName, groupIndex] = element.group.split("-");

            groups[groupName] = groups[groupName] || [];
            groups[groupName][groupIndex] = [...(groups[groupName][groupIndex] || []), element.id];
        }

        return { ...element, readValue: readValue || "", value };
    });
    return { pending, data, groups };
};

export interface PairIdField {
    id: string;
}

export interface FormMapped {
    [key: string]: string | Date | PairIdField | FormMapped;
}

export const formMapper = <Type, SchemaType>(formData: Type, formSchema: SchemaType): FormMapped[] => {
    const mappedFormData = {};

    Object.keys(formData).forEach((key) => {
        const idsPath = key.split("-");
        let id = idsPath.shift();
        let lastObject = mappedFormData;

        while (idsPath.length) {
            mappedFormData[id] = { ...mappedFormData[id] };
            lastObject = mappedFormData[id];
            id = idsPath.shift();
        }

        lastObject[id] = formData[key];

        if (formSchema[key] === FieldType.PairId) {
            lastObject[id] = { id: formData[key] } as PairIdField;
        }
    });

    return mappedFormData as FormMapped[];
};

type MapSchemaTypes = {
    pairId: string;
    string: string;
    date: Date;
    number: number;
};

export type MapSchema<T extends Record<string, keyof MapSchemaTypes>> = {
    -readonly [K in keyof T]: MapSchemaTypes[T[K]];
};

export function asSchema<T extends Record<string, keyof MapSchemaTypes>>(t: T): T {
    return t;
}

export interface StringMap {
    [key: string]: string;
}

export const defaultValues = (data: DataSchemaMapped[]): StringMap => {
    const values = {};
    data.forEach((element) => {
        values[element.id] = element.value;
    });
    return values;
};

export const defaultFormSchema = (data: DataSchemaMapped[]): Record<string, keyof MapSchemaTypes> => {
    const values = {};
    data.forEach((element) => {
        values[element.id] = element.type;
    });
    return values;
};

interface GroupComponents {
    [key: string]: DataSchemaMapped[][];
}

export const mapGroupComponents = (data: DataSchemaMapped[], groups: PanelInfoGroup): GroupComponents => {
    const groupComponents = {};
    Object.keys(groups).forEach((key) => {
        groupComponents[key] = groups[key].map((group) => {
            return group.map((insideGroup) => data.find((field) => field.id === insideGroup));
        });
    });

    return groupComponents;
};

interface GroupDefaultVisibility {
    [key: string]: boolean[][];
}

export const mapGroupDefaultVisibility = (groupComponents: GroupComponents): GroupDefaultVisibility => {
    const groupsBoolean = {};
    Object.keys(groupComponents).forEach((key) => {
        groupsBoolean[key] = groupComponents[key].map((_e, i) => {
            if (!i) {
                return true;
            }
            const components = groupComponents[key][i];
            return components.some((component) => !!component.value);
        });
    });

    return groupsBoolean;
};
